import { AppState } from './state.js';
import { CommandManager } from './commandManager.js';
import { BUTTON_IDS, TIMING } from './constants.js';

const infoModal = document.getElementById('info-modal');
const toastNotification = document.getElementById('toast-notification');
const announcer = document.getElementById('announcer');
const fullscreenBtn = document.getElementById('fullscreen-btn');
const iconEnter = document.getElementById('icon-fullscreen-enter');
const iconExit = document.getElementById('icon-fullscreen-exit');
const iconSun = document.getElementById('theme-icon-sun');
const iconMoon = document.getElementById('theme-icon-moon');
const btnBack = document.getElementById(BUTTON_IDS.BACK);
const btnForward = document.getElementById(BUTTON_IDS.FORWARD);
const btnPause = document.getElementById(BUTTON_IDS.PAUSE);
const iconPause = document.getElementById('icon-pause');
const iconPlay = document.getElementById('icon-play');
const chaosSlider = document.getElementById('chaos-slider');
const chaosDisplay = document.getElementById('chaos-display');

const SVG_NS = 'http://www.w3.org/2000/svg';

function buildSliderIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('xmlns', SVG_NS);
  svg.setAttribute('width', '24');
  svg.setAttribute('height', '24');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M2 9.5h20M7 4.5v15');
  svg.appendChild(path);
  return svg;
}

/**
 * @description Manages all direct DOM manipulation, UI feedback, and accessibility concerns.
 * This object keeps view-layer logic separate from core application logic.
 */
export const UIManager = {
  /** Announces a message to screen readers using an ARIA live region. */
  announce: (message) => {
    announcer.textContent = message;
  },

  /** Shows a short-lived toast notification at the bottom of the screen. */
  showToast: (message) => {
    toastNotification.textContent = message;
    toastNotification.classList.add('show');
    setTimeout(() => {
      toastNotification.classList.remove('show');
    }, TIMING.TOAST_DURATION);
  },

  /** Updates the fullscreen button icons based on the document's fullscreen state. */
  updateFullscreenIcons: () => {
    const isFullscreen = !!document.fullscreenElement;
    fullscreenBtn.setAttribute('aria-pressed', isFullscreen);
    iconEnter.style.display = isFullscreen ? 'none' : 'block';
    iconExit.style.display = isFullscreen ? 'block' : 'none';
  },

  /** Dynamically builds the content of the "How It Works" info modal. */
  populateInfoModal: () => {
    const infoList = infoModal.querySelector('.info-list');
    infoList.innerHTML = '';
    const controls = document.querySelectorAll(
      '.sub-menu .menu-button, .sub-menu .slider-container'
    );

    controls.forEach((control) => {
      const title = control.getAttribute('data-title') || control.title;
      if (!title) return;

      const icon = control.querySelector('svg')?.cloneNode(true);

      const shortcutMatch = title.match(/\(([^)]+)\)/);
      const cleanTitle = title.replace(/\s*\(([^)]+)\)/, '');
      const [name, description] = cleanTitle.split(': ');

      const li = document.createElement('li');
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'icon-wrapper';
      if (icon) {
        iconWrapper.appendChild(icon);
      } else if (control.classList.contains('slider-container')) {
        iconWrapper.appendChild(buildSliderIcon());
      }

      const textWrapper = document.createElement('div');
      textWrapper.className = 'info-text';

      const strong = document.createElement('strong');
      strong.textContent = `${name} `;

      if (shortcutMatch) {
        const code = document.createElement('code');
        code.textContent = shortcutMatch[1];
        strong.appendChild(code);
      }

      const span = document.createElement('span');
      span.textContent = description || '';

      textWrapper.appendChild(strong);
      textWrapper.appendChild(span);

      li.appendChild(iconWrapper);
      li.appendChild(textWrapper);
      infoList.appendChild(li);
    });
  },

  /** Opens a modal and manages focus for accessibility. */
  openModal: (modal, opener) => {
    AppState.ui.lastFocusedElement = opener || document.activeElement;
    modal.classList.add('visible');
    const explicit = modal.querySelector('[data-initial-focus], [autofocus]');
    if (explicit) {
      explicit.focus();
      return;
    }
    const focusables = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const target =
      Array.from(focusables).find(
        (el) => !el.classList.contains('modal-close-btn')
      ) || focusables[0];
    if (target) target.focus();
  },

  /** Closes a modal and returns focus to the element that opened it. */
  closeModal: (modal) => {
    modal.classList.remove('visible');
    if (AppState.ui.lastFocusedElement) AppState.ui.lastFocusedElement.focus();
  },

  /** Shows a subtle loading indicator (only for operations > 300ms) */
  showLoadingIndicator: () => {
    const container = document.getElementById('tsparticles');
    if (container) {
      container.style.cursor = 'wait';
    }
  },

  /** Hides the loading indicator */
  hideLoadingIndicator: () => {
    const container = document.getElementById('tsparticles');
    if (container) {
      container.style.cursor = '';
    }
  },

  /**
   * @description Centralized function to synchronize the entire UI with the current AppState.
   * Ensures the UI is always a perfect reflection of the application state.
   */
  syncUI: () => {
    // Theme
    const themeBtn = document.getElementById(BUTTON_IDS.THEME);
    document.body.classList.toggle('light-mode', !AppState.ui.isDarkMode);
    themeBtn.setAttribute('aria-pressed', !AppState.ui.isDarkMode);
    iconSun.style.display = AppState.ui.isDarkMode ? 'block' : 'none';
    iconMoon.style.display = AppState.ui.isDarkMode ? 'none' : 'block';

    // Toggle Buttons
    document
      .getElementById(BUTTON_IDS.GRAVITY)
      .classList.toggle('active', AppState.ui.isGravityOn);
    document
      .getElementById(BUTTON_IDS.GRAVITY)
      .setAttribute('aria-pressed', AppState.ui.isGravityOn);
    document
      .getElementById(BUTTON_IDS.WALLS)
      .classList.toggle('active', AppState.ui.areWallsOn);
    document
      .getElementById(BUTTON_IDS.WALLS)
      .setAttribute('aria-pressed', AppState.ui.areWallsOn);
    document
      .getElementById(BUTTON_IDS.CURSOR)
      .classList.toggle('active', AppState.ui.isCursorParticle);
    document
      .getElementById(BUTTON_IDS.CURSOR)
      .setAttribute('aria-pressed', AppState.ui.isCursorParticle);
    btnPause.classList.toggle('active', AppState.ui.isPaused);
    btnPause.setAttribute('aria-pressed', AppState.ui.isPaused);
    iconPause.style.display = AppState.ui.isPaused ? 'none' : 'block';
    iconPlay.style.display = AppState.ui.isPaused ? 'block' : 'none';

    // Slider
    const chaos = AppState.particleState.chaosLevel;
    chaosSlider.value = chaos;
    chaosSlider.setAttribute('aria-valuenow', chaos);
    chaosSlider.setAttribute('aria-valuetext', `Chaos level ${chaos} of 10`);
    chaosDisplay.textContent = chaos;

    // History Buttons
    btnBack.classList.toggle('disabled', CommandManager.undoStack.length === 0);
    btnForward.classList.toggle(
      'disabled',
      CommandManager.redoStack.length === 0
    );
  },
};
